import * as d3 from 'd3';
import { Component, h } from 'preact';

import { DEMOCRAT, REPUBLICAN } from '../colors';

const VACANT = '#d9d9d9';
const UP_FOR_GRABS = '#bdbdbd';
const SAFE_REPUBLICAN = '#ab261b';
const SAFE_DEMOCRAT = '#113a85';
// Drawn as an empty outline
const UNKNOWN_SEAT = { name: 'Unknown', color: '#fff', stroke: '#8c8c8c' };
const MIN_EMBED_SEAT = 22;
const MIN_EMBED_SEAT_NARROW = 28;
const NARROW_SCREEN = 480;
const MIN_EMBED_SEAT_LARGE_CHAMBER = 18;
const SMALL_SCREEN = 300;

class BalancePower extends Component {
  constructor() {
    super();

    this.state = {
      width: null,
      bodyWidth: null,
    };

    this.createGraphic = this.createGraphic.bind(this);
    this.redrawGraphic = this.redrawGraphic.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);
  }

  // Draw once the containers from render() are in the page
  componentDidMount() {
    const { className, pastData, currentData } = this.props;

    window.addEventListener('resize', this.redrawGraphic);
    this.createGraphic(className, pastData, currentData);
  }

  componentDidUpdate() {
    const { className, pastData, currentData } = this.props;

    this.createGraphic(className, pastData, currentData);
  }

  layoutSeats(total, minRows = 0) {
    const defaultRows = total >= 100 ? [5, 4, 6] : [3, 2, 4];
    const rowOptions =
      minRows > defaultRows[0] ? [minRows, minRows + 1] : defaultRows;
    const evenRows = rowOptions.find(
      option => option >= minRows && total % option == 0
    );

    if (evenRows) {
      const seats = d3.range(total).map(i => ({
        column: Math.floor(i / evenRows),
        row: i % evenRows,
      }));

      return { seats, columns: total / evenRows, rows: evenRows };
    }

    
    if (minRows <= defaultRows[0]) {
      const rows = rowOptions[0];
      const half = Math.floor(total / 2);
      const sideColumns = Math.ceil(half / rows);
      const hasMiddleSeat = total % 2 == 1;
      const columns = sideColumns * 2 + (hasMiddleSeat ? 1 : 0);

      const left = d3.range(half).map(i => ({
        column: Math.floor(i / rows),
        row: i % rows,
      }));

      const right = left
        .map(seat => ({ column: columns - 1 - seat.column, row: seat.row }))
        .reverse();

      const middle = hasMiddleSeat
        ? [{ column: sideColumns, row: Math.floor(rows / 2) }]
        : [];

      return { seats: left.concat(middle, right), columns, rows };
    }


    const columns = Math.ceil(total / rowOptions[0]);
    const fullRows = Math.floor(total / columns);
    const extra = total % columns;
    const rows = fullRows + (extra ? 1 : 0);

    const seats = [];
    d3.range(columns).forEach(column => {
      const height = fullRows + (column < extra ? 1 : 0);
      d3.range(height).forEach(row => seats.push({ column, row }));
    });

    return { seats, columns, rows };
  }

  
  drawSeats(selector, { width, total, segments, labels, center, halfway, description, fullWidth }) {
    // Embeds wrap onto as many rows as they need to keep the dots big
    let minSeat = MIN_EMBED_SEAT;
    if (total >= 100) {
      if (width <= SMALL_SCREEN) minSeat = MIN_EMBED_SEAT_LARGE_CHAMBER;
    } else if (width <= NARROW_SCREEN) {
      minSeat = MIN_EMBED_SEAT_NARROW;
    }
    const layout = seatSize => {
      const minRows = fullWidth
        ? Math.ceil(total / Math.max(1, Math.floor(width / seatSize)))
        : 0;
      return this.layoutSeats(total, minRows);
    };
    let { seats, columns, rows } = layout(minSeat);

    // Room above the seats for the majority number and the label over the seats
    const marginTop = halfway || (center && center.aboveSeats) ? 24 : 4;
    const labelLineHeight = 20;
    const labelRows = Math.max(labels.left.length, labels.right.length);
    const centerLine = center && !center.aboveSeats ? center.line : 0;
    const labelsHeight =
      6 + Math.max(labelRows, centerLine + 1) * labelLineHeight;

    // Embeds fill their container; on the results page the chart is capped
    let cell = fullWidth
      ? width / columns
      : Math.min(width / columns, 500 / columns, 36);

    // On small screens, use the biggest dots that keep the chart within the
    // height of the tallest results table, even if it leaves the chart a
    // little narrower than the embed
    const chartRoom = fullWidth && width <= SMALL_SCREEN && this.getChartRoom();
    if (chartRoom) {
      const seatsRoom = chartRoom - marginTop - labelsHeight;
      cell = Math.min(cell, seatsRoom / rows);
      for (let size = Math.floor(width); size >= 1; size--) {
        const option = layout(size);
        const optionCell = Math.min(
          width / option.columns,
          seatsRoom / option.rows
        );
        if (optionCell > cell) {
          ({ seats, columns, rows } = option);
          cell = optionCell;
        }
      }
    }
    const dotRadius = cell * 0.42;
    const chartWidth = cell * columns;
    const chartBottom = marginTop + cell * rows;
    const height = chartBottom + labelsHeight;
    const centerX = width / 2;
    const chartLeft = centerX - chartWidth / 2;

    const container = d3.select(selector);
    if (container.empty()) return;

    const svg = container
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('role', 'img')
      .attr('aria-label', description)
      // Inline, it'd add a line's descender below the room it was sized for
      .style('display', chartRoom ? 'block' : null);

    // If the counts fall short of the chamber, the seats nobody accounts for
    // are left empty in the middle so both parties stay pinned to their edges
    const unknown = Math.max(0, total - d3.sum(segments, d => d.seats));
    const seatSegments = [];
    segments.forEach(segment => {
      for (let i = 0; i < segment.seats; i++) seatSegments.push(segment);
      if (segment.middle) {
        for (let i = 0; i < unknown; i++) seatSegments.push(UNKNOWN_SEAT);
      }
    });

    // When the open seats are labeled from above, move them to the top of
    // their column so the label sits right over them. Any seats from the
    // left-hand party that share the column drop to the bottom of it. If
    // both parties share the column, the open seats go between them instead,
    // with the right-hand party on top.
    if (center && center.aboveSeats) {
      const middleIndex = segments.findIndex(segment => segment.middle);
      const isLeft = segment => segments.indexOf(segment) < middleIndex;

      d3.range(columns).forEach(column => {
        const indexes = d3
          .range(seatSegments.length)
          .filter(i => seats[i] && seats[i].column == column)
          .sort((a, b) => seats[a].row - seats[b].row);
        const inColumn = indexes.map(i => seatSegments[i]);

        if (!inColumn.some(segment => segment.middle)) return;

        const open = inColumn.filter(segment => segment.middle);
        const right = inColumn.filter(
          segment => !segment.middle && !isLeft(segment)
        );
        const left = inColumn.filter(segment => isLeft(segment));

        (left.length && right.length
          ? right.concat(open, left)
          : open.concat(right, left)
        ).forEach((segment, i) => (seatSegments[indexes[i]] = segment));
      });
    }

    svg
      .append('g')
      .selectAll('circle')
      .data(seats.filter((seat, i) => seatSegments[i]))
      .enter()
      .append('circle')
      .attr('class', 'balance-seat')
      .attr('cx', d => chartLeft + cell * (d.column + 0.5))
      .attr('cy', d => marginTop + cell * (d.row + 0.5))
      .attr('r', dotRadius)
      .attr('fill', (d, i) => seatSegments[i].color)
      .attr('stroke', (d, i) => seatSegments[i].stroke || 'none')
      .append('title')
      .text((d, i) => seatSegments[i].name);

    // Whichever party's seats cross the middle of the chart has the majority
    let majorityText;

    if (halfway) {
      majorityText = svg
        .append('text')
        .attr('class', 'text-majority')
        .attr('text-anchor', 'middle')
        .attr('x', centerX)
        .attr('y', 14)
        .style('fill', '#000')
        .text(halfway);

      svg
        .append('line')
        .attr('class', 'annotation-line')
        .attr('x1', centerX)
        .attr('x2', centerX)
        .attr('y1', 19)
        .attr('y2', chartBottom + 2);
    }

    const labelTop = chartBottom + 6;

    [
      { lines: labels.left, x: chartLeft, anchor: 'start' },
      { lines: labels.right, x: chartLeft + chartWidth, anchor: 'end' },
    ].forEach(({ lines, x, anchor }) => {
      svg
        .append('g')
        .selectAll('text')
        .data(lines)
        .enter()
        .append('text')
        .attr('class', (d, i) => (i == 0 ? 'bar-title' : 'bar-title-uncontested'))
        .attr('text-anchor', anchor)
        .attr('x', x)
        .attr('y', (d, i) => labelTop + 15 + i * labelLineHeight)
        .style('fill', d => d.color)
        .text(d => d.text);
    });

    if (!center) return;

    const centerText = svg
      .append('text')
      .attr('class', 'balance-center-label')
      .attr('text-anchor', 'middle')
      .attr('x', centerX)
      .text(center.text);

    // Centered on its own line under the party labels
    if (!center.aboveSeats) {
      centerText.attr('y', labelTop + 15 + center.line * labelLineHeight);
      return;
    }

    // Above the chart, starting at the left edge of its seats
    const textWidth = centerText.node().getComputedTextLength();

    let x =
      d3.min(
        seats.filter((seat, i) => seatSegments[i] && seatSegments[i].middle),
        d => chartLeft + cell * (d.column + 0.5)
      ) - dotRadius;

    // Keep it inside the chart and clear of the majority number
    x = Math.max(chartLeft, Math.min(x, chartLeft + chartWidth - textWidth));

    if (majorityText) {
      const clearance = majorityText.node().getComputedTextLength() / 2 + 8;

      if (x < centerX + clearance && x + textWidth > centerX - clearance) {
        x =
          x + textWidth / 2 < centerX
            ? centerX - clearance - textWidth
            : centerX + clearance;
      }
    }

    centerText.attr('text-anchor', 'start').attr('x', x).attr('y', 14);
  }

  createGraphic(className, pastData, currentData) {
    let { width } = this.getContainerWidth(className);

    const pastVacant = pastData.vacant || 0;
    const isSenate = className == 'texas-senate';
    // The U.S. House chart is only the Texas delegation, so it has no majority
    const halfway = className != 'us-house' ? currentData.halfway : null;

    function getZeroUncontested(num) {
      return num > 0 ? num : 'None';
    }

    function seatText(num) {
      return `${num} seat${num == 1 ? '' : 's'}`;
    }

    d3.selectAll(`.${className}-graphic-previous svg`).remove();

    d3.selectAll(`.${className}-graphic-current svg`).remove();

    // Currently vacant seats sit between Republicans and Democrats
    this.drawSeats(`.${className}-graphic-previous`, {
      width,
      total: pastData.total,
      halfway,
      segments: [
        { name: 'Republican', seats: pastData.republican, color: REPUBLICAN },
        { name: 'Vacant', seats: pastVacant, color: VACANT, middle: true },
        { name: 'Democrat', seats: pastData.democrat, color: DEMOCRAT },
      ],
      labels: {
        left: [{ text: `${pastData.republican} Republicans`, color: REPUBLICAN }],
        right: [{ text: `${pastData.democrat} Democrats`, color: DEMOCRAT }],
      },
      center: pastVacant > 0 && {
        text: `${pastVacant} vacant`,
        aboveSeats: true,
      },
      description: `${pastData.republican} Republicans, ${pastData.democrat} Democrats and ${seatText(pastVacant)} vacant`,
    });

    // Seats that weren't in play (uncontested or not up for election) sit on
    // the outside in a darker shade, with the undecided seats in the middle
    const safeRepublican =
      currentData.uncontested_republican +
      pastData.republican_not_up_for_election;
    const safeDemocrat =
      currentData.uncontested_democrat + pastData.democrat_not_up_for_election;
    const republican = currentData.republican + safeRepublican;
    const democrat = currentData.democrat + safeDemocrat;

    // Small embeds abbreviate the parties ("X R") so the labels fit
    const short = this.props.fullWidth && width <= SMALL_SCREEN;

    const leftLabels = [
      { text: short ? `${republican} R` : `${republican} Republicans`, color: REPUBLICAN },
      {
        text: `${getZeroUncontested(currentData.uncontested_republican)} uncontested`,
        color: SAFE_REPUBLICAN,
      },
    ];

    const rightLabels = [
      { text: short ? `${democrat} D` : `${democrat} Democrats`, color: DEMOCRAT },
      {
        text: `${getZeroUncontested(currentData.uncontested_democrat)} uncontested`,
        color: SAFE_DEMOCRAT,
      },
    ];

    if (isSenate) {
      leftLabels.push({
        text: `${pastData.republican_not_up_for_election} not up for election`,
        color: SAFE_REPUBLICAN,
      });
      rightLabels.push({
        text: `${pastData.democrat_not_up_for_election} not up for election`,
        color: SAFE_DEMOCRAT,
      });
    }

    const safeName = isSenate
      ? 'uncontested or not up for election'
      : 'uncontested';

    this.drawSeats(`.${className}-graphic-current`, {
      width,
      fullWidth: this.props.fullWidth,
      total: currentData.total,
      halfway,
      segments: [
        { name: `Republican, ${safeName}`, seats: safeRepublican, color: SAFE_REPUBLICAN },
        { name: 'Republican', seats: currentData.republican, color: REPUBLICAN },
        { name: 'Up for grabs', seats: currentData.vacant, color: UP_FOR_GRABS, middle: true },
        { name: 'Democrat', seats: currentData.democrat, color: DEMOCRAT },
        { name: `Democrat, ${safeName}`, seats: safeDemocrat, color: SAFE_DEMOCRAT },
      ],
      labels: { left: leftLabels, right: rightLabels },
      // On its own line so it can't run into the labels on either side. The
      // embeds show it under the title instead.
      center: !this.props.currentOnly && currentData.vacant > 0 && {
        text: `${currentData.vacant} up for grabs`,
        line: leftLabels.length,
      },
      description: `${republican} Republicans, ${democrat} Democrats and ${seatText(currentData.vacant)} still up for grabs`,
    });
  }

  // Height the embed's min-height (the tallest results table, in
  // _embed.scss) leaves for this chart, measured while the chart is removed
  getChartRoom() {
    const body = document.body;
    const style = getComputedStyle(body);
    const minHeight = parseFloat(style.minHeight);
    if (!minHeight) return null;

    let used =
      parseFloat(style.paddingTop) +
      parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) +
      parseFloat(style.borderBottomWidth);
    Array.from(body.children).forEach(child => {
      if (getComputedStyle(child).position != 'absolute') {
        used += child.getBoundingClientRect().height;
      }
    });

    const room = minHeight - used;
    return room > 0 ? room : null;
  }

  getContainerWidth(className) {
    let base;

    const body = d3.select('body');

    base = d3.select(`.${className}-balance-graphic`);

    const width = base.node().offsetWidth;
    const bodyWidth = body.node().offsetWidth;

    return { width, bodyWidth };
  }

  redrawGraphic() {
    const { className, pastData, currentData } = this.props;

    d3.selectAll(`.${className}-graphic-previous svg`).remove();
    d3.selectAll(`.${className}-graphic-current svg`).remove();

    let { width, bodyWidth } = this.getContainerWidth(className);

    this.setState({ width: width, bodyWidth: bodyWidth });
    this.createGraphic(className, pastData, currentData);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.redrawGraphic);
  }

  render() {
    const { className, chamber, currentOnly, currentData } = this.props;

    // Embeds show only the current breakdown, without the narrative text
    if (currentOnly) {
      return (
        <div id="balance-graphics">
          {currentData.vacant > 0 && (
            <p class="embed-balance__up-for-grabs">
              {`${currentData.vacant} up for grabs`}
            </p>
          )}
          <div class={`${className}-graphic-current`}></div>
        </div>
      );
    }

    const chamberText =
      chamber == 'U.S. House'
        ? 'the Texas delegation in'
        : 'the balance of power in';

    return (
      <div id="balance-graphics">
        <p class="t-copy copy--narrative">
          {`Going into the November election, ${chamberText} the ${chamber} was broken down like this: `}
        </p>
        <div class={`${className}-graphic-previous`}></div>
        <p class="t-copy copy--narrative">
          {`Here how it'll look starting in January:`}
        </p>
        <div class={`${className}-graphic-current`}></div>
      </div>
    );
  }
}

export default BalancePower;
